import { ENUM_LABELS, type TestProcedureRow } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { DetailList } from '#client/components/ui/DetailList';
import { EmptyState } from '#client/components/ui/EmptyState';
import { printUrls } from '#client/lib/endpoints';
import { routes } from '#client/lib/routes';
import { formatBaseQuantity } from '#client/lib/format';
import { procedureDetails } from './procedure-card.logic';
import { ButtonLink } from '#client/components/ui/ButtonLink';

type ProcedureRecipe = NonNullable<TestProcedureRow['parameter']>['recipes'][number];

/**
 * One standard test procedure, as the bench reads it.
 *
 * The reagent quantities come from `ReagentRecipe`, not from the procedure
 * text. The same sentence in the lab's Word SOP is split so that "400 µL R1 +
 * 100 µL R2" drives the stock deduction and the cost per test, while "500 µL WR
 * + 10 µL sample" stays prose — sample volume is drawn from the patient, not off
 * the shelf. Showing both together here is what makes that split legible rather
 * than surprising.
 */
export function TestProcedureCard({
  procedure,
  canWrite,
}: {
  procedure: TestProcedureRow | null;
  canWrite: boolean;
}) {
  if (!procedure) return <NoProcedureCard canWrite={canWrite} />;

  const recipes = procedure.parameter?.recipes ?? [];

  return (
    <div className="c-form-stack">
      <ProcedureSummaryCard procedure={procedure} canWrite={canWrite} />

      {procedure.procedure_steps.length > 0 && (
        <Card title="Steps">
          <ol className="c-procedure-steps">
            {procedure.procedure_steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
        </Card>
      )}

      {recipes.length > 0 && <RunConsumptionCard recipes={recipes} />}
    </div>
  );
}

function NoProcedureCard({ canWrite }: { canWrite: boolean }) {
  return (
    <Card title="Procedure">
      <EmptyState
        title="No procedure recorded"
        body="The method, specimen, steps and reagent volumes the bench follows for this test."
        action={
          canWrite ? (
            <ButtonLink variant="primary" size="md" to={routes.testProcedureNew}>
              Write one
            </ButtonLink>
          ) : undefined
        }
      />
    </Card>
  );
}

function ProcedureSummaryCard({
  procedure,
  canWrite,
}: {
  procedure: TestProcedureRow;
  canWrite: boolean;
}) {
  return (
    <Card
      title={procedure.title}
      subtitle={`Version ${procedure.version}`}
      actions={
        <div className="c-card__actions">
          <a
            className="c-button c-button--secondary c-button--sm"
            href={printUrls.procedure(procedure.id)}
          >
            Print
          </a>
          {canWrite && (
            <ButtonLink variant="secondary" size="sm" to={routes.testProcedureEdit(procedure.id)}>
              Edit
            </ButtonLink>
          )}
        </div>
      }
    >
      <DetailList items={procedureDetails(procedure)} />
    </Card>
  );
}

function RunConsumptionCard({ recipes }: { recipes: readonly ProcedureRecipe[] }) {
  return (
    <Card
      title="What one run consumes"
      subtitle="From the reagent recipes — this is what the stock ledger deducts"
    >
      <ul className="c-plain-list">
        {recipes.map((recipe) => (
          <li key={recipe.id} className="c-plain-list__row">
            <span className="c-plain-list__name">
              {recipe.item.name}
              <span className="c-inventory__code">{recipe.item.code}</span>
            </span>
            <span className="tnum c-plain-list__value">
              {formatBaseQuantity(Number(recipe.quantity_base))}{' '}
              {ENUM_LABELS.stockUnit[recipe.item.base_unit]}
            </span>
          </li>
        ))}
      </ul>
      <p className="c-field__help">
        Sample volume is not on this list: it is drawn from the patient, not off the shelf.
      </p>
    </Card>
  );
}
